import React, { useCallback, useEffect, useRef, useState } from 'react';
import './style.scss';
import { loader } from 'graphql.macro';
import { productServiceClient } from '../../../_shared/apolloClientUtils';
import { getIdFromShopifyGraphQLId } from 'src/scripts/utils/shopify';
import {
  GetProductsByProductIdsQueryVariables,
  GetTheLookProductFragmentFragment,
} from '../../../../../generated/product-service-graphql';
import { checkIfItemCanBeSoldOut, getOnlineSalePricing } from 'src/scripts/utils/helpers';
import { toLocalCurrencyShowEstimate } from 'src/scripts/utils/currency';
import { Swiper, SwiperSlide } from 'swiper/react';
import '../../../../templates/product-listing-page/quick-add/quick-add-styles.scss';
import {
  dispatchOpenVariantSelectorDropdown,
  fetchGetTheLookExtendedData,
} from 'src/scripts/sections/header/Cart/components/CartImpulseBuy/utils';
import { addVariant } from 'src/scripts/utils/cart';
import { GetTheLookProductFragment } from '../../../../../generated/shopify-graphql';
import { E_GIFT_CARD_PRODUCT_TYPE } from '../../../../utils/constants';
// extract to utils
import { checkIfProductHasEarlyAccessTags } from 'src/scripts/templates/product-listing-page/PlpUtils/ProcessBadgeDiscount';

const GetGetTheLookProductsByProductIds = loader('./GetGetTheLookProductsByProductIds.product-service.graphql');

const GetTheLookPDP = ({
  productIds,
  showEarlyAccess = false,
}: {
  productIds: string[];
  showEarlyAccess?: boolean;
}) => {
  const [products, setProducts] = useState<GetTheLookProductFragmentFragment[]>([]);
  const [extendedData, setExtendedData] = useState<GetTheLookProductFragment[]>([]);
  const [loading, setLoading] = useState(true);
  const getTheLookRef = useRef<HTMLDivElement | null>(null);

  const getGetTheLookProducts = useCallback(
    async (productIds: string[]) => {
      // ? [REVISIT] this need a large refactor, these can be paralized
      const response = await productServiceClient.query({
        query: GetGetTheLookProductsByProductIds,
        variables: {
          productIds,
        } as GetProductsByProductIdsQueryVariables,
      });
      const products: GetTheLookProductFragmentFragment[] = response?.data?.productsByProductIds || [];
      const productGids = products.map((product) => product.gid);
      const extendedDataResults = await fetchGetTheLookExtendedData({ ids: productGids });
      const filteredExtendedDataResults = extendedDataResults;
      const filteredProducts = products.filter((product) => {
        // sf ~ storefront api data is live & reflects immediately
        const sfApiData = filteredExtendedDataResults?.find((data) => data.id === product.gid);
        // dupe logic that needs to be consolidated as part of data fetching utility, just 1 example: LND-123
        return (
          sfApiData &&
          sfApiData.onlineStoreUrl &&
          (showEarlyAccess || !checkIfProductHasEarlyAccessTags(sfApiData.tags))
        );
      });
      setProducts(filteredProducts);
      setExtendedData(filteredExtendedDataResults);
      setLoading(false);
    },
    [showEarlyAccess]
  );

  useEffect(() => {
    getGetTheLookProducts(productIds);
  }, [getGetTheLookProducts, productIds]);

  const breakpoints = {
    1060: {
      slidesPerView: 3.5,
    },
    320: {
      slidesPerView: 2.5,
    },
  };

  if (products.length > 0 && !loading) {
    return (
      <div className="get-the-look-pdp d-flex">
        <p className="get-the-look-pdp-title">Shop the Look</p>
        <div className="get-the-look-products d-flex" ref={getTheLookRef}>
          <Swiper
            spaceBetween={12}
            slidesPerView={1.15}
            breakpoints={breakpoints}
            allowTouchMove
            scrollbar={{ draggable: true }}
            cssMode={true}
            speed={200}
          >
            {products.map((product) => {
              // Find corresponding extendedData for the current product
              const matchingExtendedData = extendedData.find((data) => data.id === product.gid);

              if (matchingExtendedData) {
                return (
                  <React.Fragment key={product.gid}>
                    <SwiperSlide>
                      <GetTheLookProductTile
                        product={product}
                        extendedData={matchingExtendedData}
                        getTheLookRef={getTheLookRef}
                      />
                    </SwiperSlide>
                  </React.Fragment>
                );
              }

              return null;
            })}
          </Swiper>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

export default GetTheLookPDP;

let GetTheLookProductTile = ({
  product,
  extendedData,
  getTheLookRef,
}: {
  product: GetTheLookProductFragmentFragment;
  extendedData: GetTheLookProductFragment | undefined;
  getTheLookRef?: React.RefObject<HTMLElement>;
}) => {
  if (!extendedData) {
    return null;
  }
  const image = product.images?.[0] || product.images?.[1];
  const displayPrice = product.priceRange.maxVariantPrice.amount;
  const actualPrice = product.compareAtPriceRange.maxVariantPrice.amount;
  const onlineSalePrice = product.tags ? getOnlineSalePricing(product.tags, displayPrice) : undefined;
  const itemIsOnSale =
    (onlineSalePrice !== undefined && actualPrice > 0) || (displayPrice < actualPrice && displayPrice !== 0);
  const priceToRender = onlineSalePrice || displayPrice;
  const tags = product.tags;
  const isSoldOut = checkIfItemCanBeSoldOut(tags) ? !extendedData?.availableForSale : false;
  const sizesData = extendedData.variants.edges.map((variantEdge) => ({
    isSizeAvailable: variantEdge.node.availableForSale,
    quantityAvailable: variantEdge.node.quantityAvailable,
    value: variantEdge.node.selectedOptions.find((item) => item.name === 'Size')?.value,
    variantId: getIdFromShopifyGraphQLId(variantEdge.node.id),
    sku: variantEdge.node.sku,
    price: variantEdge.node.priceV2,
    compareAtPrice: variantEdge.node.compareAtPriceV2,
  }));
  const singleVariant = sizesData[0];
  const productId = getIdFromShopifyGraphQLId(product.gid);
  const variantPricesData = {
    id: Number(singleVariant?.variantId),
    price: Number(singleVariant?.price.amount),
    displayPrice: Number(singleVariant?.compareAtPrice?.amount),
    tags: product.tags,
    productId: Number(productId),
  };
  const isAtLeastOneSizeAvailable = sizesData.some((size) => size.isSizeAvailable);
  const isEGiftCard = product.productType.toLowerCase() === E_GIFT_CARD_PRODUCT_TYPE;
  const isQuickAddEnabled = !isSoldOut && !isEGiftCard;

  const handleQuickAdd = async (event: { currentTarget: Element }) => {
    const button = event.currentTarget;
    const shouldShowATCmodal = true;
    const ATBtype = 'Shop the look';
    try {
      if (sizesData.length === 1 && singleVariant?.isSizeAvailable) {
        addVariant(singleVariant?.variantId, variantPricesData, { shouldShowATCmodal, ATBtype });
      } else if (isAtLeastOneSizeAvailable) {
        dispatchOpenVariantSelectorDropdown({
          sizesData: sizesData.map((item) => ({ ...item, sku: item.sku ?? '' })),
          productType: extendedData.productType,
          variantPricesData,
          mountNode: button as HTMLElement,
          shouldShowATCmodal,
          ATBtype,
          renderTargetContainer: getTheLookRef?.current,
        });
      }
    } catch (err) {
      console.error('Failed to dispatch variant selector for quick add:', err);
    }
  };

  return (
    <div className="product-tile">
      <div className="position-relative">
        <a
          className="aspect-ratio-3x2-wrapper image-wrapper"
          href={product.handle && `/products/${product.handle}`}
          tabIndex={-1}
        >
          {image && <img src={image} alt={product.title} />}
        </a>
        {isQuickAddEnabled && <QuickAddToCart onClickCallback={handleQuickAdd} />}
      </div>

      <div className="get-the-look-product-info d-flex flex-column">
        <a
          className="get-the-look-product d-flex"
          href={product.handle && `/products/${product.handle}`}
          key={product.handle}
          data-product-link={product.handle && `${window.location.origin}/products/${product.handle}`}
        >
          {product.title && <div className="get-the-look-product-info__title small-p">{product.title}</div>}
        </a>

        <div className="get-the-look-product-info__price small-p">
          <span
            className={`product__price d-none display_price ${
              itemIsOnSale ? 'get-the-look-product-info__price__sale' : ''
            }`}
          >
            {toLocalCurrencyShowEstimate(priceToRender)}
          </span>
          {itemIsOnSale && (
            <span className="product__price d-none display_price get-the-look-product-info__price__full">
              {toLocalCurrencyShowEstimate(actualPrice)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const QuickAddToCart = ({ onClickCallback }: { onClickCallback: (e: React.MouseEvent) => void }) => {
  return (
    <div className="quick-add-to-cart">
      <div
        className="quick-add-button-container"
        onClick={(e) => onClickCallback(e)}
        data-testid="get-the-look-atc-button"
      >
        <span>
          <span className="alo-quick-add-icon"></span>
        </span>
      </div>
    </div>
  );
};

export { GetTheLookProductTile };
